import withTranslations from "../../../../../hocs/withTranslations";
import NewModal from "../../../modal/NewModal";
import classNames from "classnames";
import PlansToggleSelection from "./PlansToggleSelection";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Loader from "../../../common/Loader";
import withPaymentDetails from "../../../../../hocs/withPaymentDetails";
import withUserData from "../../../../../hocs/withUserData";
import useNextPlans from "../../../../../hooks/useNextPlans";
import { compose } from "redux";
import upgradeIcon from "../../../landingPage/components/LandingTypography/assets/upgrade_icon.svg";
import NoPermissionPayment from "./NoPermissionPaymentModal";
import getNoPermissionModalPlanDescription from "../utils/getNoPermisionModalPlanDescription";
import PlanBoxSquare from "../../../landingPage/pages/Plans/components/PlanBoxSquare";
import useIsDesktop from "../../../landingPage/hooks/useIsDesktop";
import { UrlProvider } from "../../../../../api/UrlProvider";
import { withRouter } from "react-router-dom";
import useAutoSelectHighestPlan from "../hooks/useAutoSelectHighestPlan";
import {
  getAllowedPeriods,
  getAllowPeriodChange,
  getDefaultPeriod,
  isPeriodAllowed,
  KNOWN_PERIODS
} from "../../../../../lib/Plans";
import {
  getRegisterWithRedirectUrl,
  getReturnUrl
} from "../../../../../lib/auth/authRedirectUrl";
import { getInitialAuthCheckPending } from "../../../../../redux/modules/initialAuthCheck/selector";
import { getFetchCnt } from "../../../../../redux/modules/translations/selector";

const REGISTER_REQUIRED_TOAST_KEY = "register_required_to_view_content_toast";

const NO_PERMISSIONS_STEPS = {
  PLANS: "PLANS",
  PAYMENT: "PAYMENT"
};

const NoPermissionModal = ({
  translate,
  translationExists,
  locale,
  permissions,
  hideModal,
  paymentDetails,
  loading: paymentDetailsLoading,
  history,
  onClose,
  userData
}) => {
  const isDesktop = useIsDesktop();
  const authPending = useSelector(getInitialAuthCheckPending);
  const translationsFetchCnt = useSelector(getFetchCnt);
  const translationsLoaded = translationsFetchCnt > 0;
  const blockPlans = authPending || !userData;

  const { response, loading, error } = useNextPlans({
    permissions: blockPlans ? [] : permissions,
    locale
  });

  const [selectedPlan, setSelectedPlan] = useState();
  const [step, setStep] = useState(NO_PERMISSIONS_STEPS.PLANS);
  const [period, setPeriod] = useState(KNOWN_PERIODS.YEARLY);

  const plans = useMemo(() => (Array.isArray(response) ? response : []), [
    response
  ]);

  const allowedPeriods = useMemo(() => getAllowedPeriods(plans), [plans]);
  const allowPeriodChange = getAllowPeriodChange(
    allowedPeriods,
    paymentDetails
  );

  useAutoSelectHighestPlan({
    plans,
    currentPeriod: period,
    selectedPlan,
    setSelectedPlan
  });

  useEffect(() => {
    if (!error && Array.isArray(response) && response.length > 0) {
      setSelectedPlan(response[0]);
    }
  }, [response, error]);

  useEffect(() => {
    if (!allowedPeriods.length) {
      return;
    }

    const defaultPeriod = getDefaultPeriod(allowedPeriods);
    if (
      defaultPeriod != null &&
      !isPeriodAllowed(period, allowedPeriods)
    ) {
      setPeriod(defaultPeriod);
    }
  }, [allowedPeriods]);

  useEffect(() => {
    if (authPending || !translationsLoaded || userData) {
      return;
    }

    if (typeof hideModal === "function") {
      hideModal();
    }

    const registerUrlOptions = {
      locale,
      returnUrl: getReturnUrl()
    };

    if (
      typeof translationExists === "function" &&
      translationExists(REGISTER_REQUIRED_TOAST_KEY)
    ) {
      registerUrlOptions.toastMessage = translate(REGISTER_REQUIRED_TOAST_KEY);
    }

    history.replace(getRegisterWithRedirectUrl(registerUrlOptions));
  }, [
    authPending,
    translationsLoaded,
    userData,
    hideModal,
    history,
    locale,
    translate,
    translationExists
  ]);

  if (blockPlans) {
    return <Loader />;
  }

  const title = (
    <>
      {isDesktop ? (
        <img
          alt={""}
          src={upgradeIcon}
          width={24}
          height={24}
          className={"mr-3"}
        />
      ) : null}
      <span>{translate("plans_premium-modal_title")}</span>
    </>
  );

  const handleSetPeriod = nextPeriod => {
    if (allowPeriodChange && typeof nextPeriod === "number") {
      setPeriod(nextPeriod);
    }
  };

  if (error || !permissions?.length) {
    if (error?.response?.status === 404) {
      return (
        <NewModal hideModal={hideModal} title={title} onClose={onClose}>
          <p>{translate("plans_no-offers")}</p>
        </NewModal>
      );
    }

    return (
      <NewModal
        hideModal={hideModal}
        title={title}
        onClose={onClose}
        acceptButtonText={translate("no-permission-modal_cancel-button-text")}
        onCancel={() => {
          history.push(
            UrlProvider.getUrl("fe.plans.mainPage", {
              locale
            })
          );
        }}
      >
        <p>{translate("no-permission-modal_error-message")}</p>
      </NewModal>
    );
  }

  if (loading !== false || paymentDetailsLoading !== false) {
    return (
      <NewModal hideModal={hideModal} title={title} onClose={onClose}>
        <Loader />
      </NewModal>
    );
  }

  if (step === NO_PERMISSIONS_STEPS.PAYMENT) {
    return (
      <NoPermissionPayment
        selectedPlan={selectedPlan}
        period={period}
        paymentDetails={paymentDetails}
        hideModal={hideModal}
        onCancel={() => {
          setStep(NO_PERMISSIONS_STEPS.PLANS);
        }}
        onClose={onClose}
      />
    );
  }

  return (
    <NewModal
      canHide={true}
      title={title}
      acceptButtonText={translate("plans_payment-bar_upgrading")}
      onAccept={() => {
        setStep(NO_PERMISSIONS_STEPS.PAYMENT);
      }}
      hideOnAccept={false}
      cancelButtonText={translate("no-permission-modal_cancel-button-text")}
      onCancel={() => {
        history.push(
          UrlProvider.getUrl("fe.plans.mainPage", {
            locale
          })
        );
      }}
      hideModal={hideModal}
      onClose={onClose}
    >
      <p className={"text-sm text-black opacity-70 mb-6"}>
        {translate("plans_premium-modal_paragraph")}
      </p>
      <div
        className={classNames("flex align-center gap-3 mb-12", {
          "justify-end": isDesktop
        })}
      >
        <strong className={"text-xs font-medium m-0 text-black-4"}>
          {translate("plans_toggle_title")}
        </strong>
        <div>
          <PlansToggleSelection
            period={period}
            allowedPeriods={allowedPeriods}
            allowPeriodChange={allowPeriodChange}
            handleSetPeriod={handleSetPeriod}
          />
        </div>
      </div>
      <div className={"flex flex-row gap-4"}>
        {plans.slice(0, isDesktop ? plans.length : 1).map((plan, index) => {
          return (
            <div key={plan.id} className={"flex-1 flex"}>
              <PlanBoxSquare
                isSelectedPlan={selectedPlan?.id === plan.id}
                onSelect={() => setSelectedPlan(plan)}
                period={period}
                description={getNoPermissionModalPlanDescription({
                  plan,
                  translationExists,
                  translate
                })}
                isAvailableInPlan={index === 0}
                plan={plan}
                isHighlightedPlan={plan.is_highlighted}
                isRecommendedPlan={plan.is_recommended}
              />
            </div>
          );
        })}
      </div>
    </NewModal>
  );
};

export default compose(
  withTranslations,
  withPaymentDetails,
  withRouter,
  withUserData
)(NoPermissionModal);
